import { Typography, theme } from "antd";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type { IChartBlock } from "./chat-block.types";
import { formatByUnit } from "./format";

const { Text } = Typography;

type Props = {
	block: IChartBlock;
};

// Inline chart, recharts. Only the allow-listed variants render; `waterfall`
// falls back to bars until a dedicated renderer lands (documented, not silent —
// it still draws the data). Values are numeric in the unit's natural scale
// (money = major units); axis + tooltip format via format.ts.
export function ChartBlock({ block }: Props) {
	const { token } = theme.useToken();
	const color = token.colorPrimary;
	const data = block.series.map((s) => ({ label: s.label, value: s.value }));
	const fmt = (value: number) => formatByUnit(value, block.unit);
	// Axis ticks are a scale, not an amount — whole dollars keep them legible.
	const fmtTick = (value: number) => formatByUnit(value, block.unit, false);

	// Emphasize the peak bar and mute the rest, so the chart reads at a glance
	// instead of a uniform wall of green.
	const maxIdx = data.reduce(
		(mi, d, i, arr) => (d.value > arr[mi].value ? i : mi),
		0,
	);

	const axes = (
		<>
			<CartesianGrid
				strokeDasharray="3 3"
				stroke={token.colorBorderSecondary}
				vertical={false}
			/>
			<XAxis
				dataKey="label"
				tick={{ fontSize: 11, fill: token.colorTextSecondary }}
			/>
			<YAxis
				tick={{ fontSize: 11, fill: token.colorTextSecondary }}
				tickFormatter={fmtTick}
				width={64}
			/>
			<Tooltip formatter={(value: number) => fmt(value)} />
		</>
	);

	const renderChart = () => {
		switch (block.variant) {
			case "line":
				return (
					<LineChart data={data}>
						{axes}
						<Line
							type="monotone"
							dataKey="value"
							stroke={color}
							strokeWidth={2}
							dot={false}
						/>
					</LineChart>
				);
			case "area":
				return (
					<AreaChart data={data}>
						{axes}
						<Area
							type="monotone"
							dataKey="value"
							stroke={color}
							fill={color}
							fillOpacity={0.15}
						/>
					</AreaChart>
				);
			default:
				// bar + waterfall fallback
				return (
					<BarChart data={data}>
						{axes}
						<Bar dataKey="value" radius={[4, 4, 0, 0]}>
							{data.map((entry, i) => (
								<Cell
									key={`${entry.label}-${entry.value}`}
									fill={color}
									fillOpacity={i === maxIdx ? 1 : 0.4}
								/>
							))}
						</Bar>
					</BarChart>
				);
		}
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
			{block.title ? (
				<Text strong style={{ fontSize: 13 }}>
					{block.title}
				</Text>
			) : null}
			<div style={{ width: "100%", height: 190 }}>
				<ResponsiveContainer width="100%" height="100%">
					{renderChart()}
				</ResponsiveContainer>
			</div>
		</div>
	);
}
